// apiActions.js
import axios from 'axios';
import { commonHeaders } from './commonHeader';
import config from '../web3/web3Config.json';
import Cookies from "universal-cookie";
import db from '../db/db-service';
import { generateOpenApiToken } from '../auth/cryptoToken';
import { getValidAccessToken } from '../auth/tokenGuard';
import { toast, Bounce } from "react-toastify";
import { encryptFiles } from '../helper/attachmentHelper';
import { reportError, toErrorString } from '../utils/errorLogger';
const cookies = new Cookies();

const BASE_URL = config["EMAIL_API"];
commonHeaders();

/**
 * Failure sentinel returned by fetchAPI when a request does not complete.
 *
 * LOAD-BEARING: this used to be `[]`. An empty array is indistinguishable from a legitimately empty
 * payload, so a failed /getInsecureEmails rendered as "you have no mail" and a failed key read looked
 * like "you have no key" — the same silent-data-loss shape already fixed in dcontacts
 * (dcontacts/src/server/server.ts). Never go back to returning `[]` (or any other "empty" value) here:
 * callers MUST be able to tell "the server said no" apart from "the server said nothing".
 *
 * Shape: { __error: true, status: number|null, message: string }. Consumers must test with
 * isApiError() — do NOT poke at `__error` directly.
 */

/** True when `v` is the fetchAPI failure sentinel above (i.e. the request did NOT succeed). */
export function isApiError(v) {
    return !!v && typeof v === 'object' && v.__error === true;
}

export async function fetchAPI(endpoint, method, body = null, openAPIToken = null) {
    try {
        const token = getValidAccessToken();
        const response = await axios({
            method: method,
            url: `${BASE_URL}/${endpoint}`,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
                ...(openAPIToken && { 'x-api-token': openAPIToken })
            },
            data: body
        });
        return response.data;
    } catch (error) {
        console.error(`Error fetching :`, error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '-',
            message: `FileName: api-action.js, Fun: fetchAPI, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        if (error?.response?.status === 401) {
            cookies.remove('accessToken');
            localStorage.removeItem('accessToken');
            window.open(`/app`, "_self");
        }
        // 429 is a TRANSIENT rate limit, not an authentication failure. This used to delete the access
        // token and bounce the user to /app (the connect-wallet screen), so a brief burst of traffic
        // silently signed people out mid-session — you would log in, land on the inbox, see
        // "Too many requests" and be thrown back to connect-wallet. Keep the session intact and let the
        // caller retry; only genuine auth failures (401 above) may clear credentials.
        if (error.response?.status === 429) {
            toast.error('Too many requests — please wait a moment and try again.', {
                position: "top-center",
                transition: Bounce,
                // One toast, not one per in-flight request: the inbox fires several calls at once, so a
                // single rate-limited burst was stacking an identical toast for every one of them.
                toastId: 'rate-limited',
            });
        }
        // Distinguishable failure sentinel — see the doc comment above isApiError(). The 401/429
        // branches above are untouched; this only replaces the old `return []`.
        return {
            __error: true,
            status: error?.response?.status ?? null,
            message:
                error?.response?.data?.message ||
                error?.message ||
                'Request failed. Please check your connection and try again.'
        };
    }
}
async function fetchFiles(endpoint, method, body = null) {
    try {
        const token = getValidAccessToken();

        const response = await axios({
            method: method,
            url: `${BASE_URL}/${endpoint}`,
            headers: {
                'Accept': 'application/zip',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            responseType: 'blob',
            data: body
        })
        return response.data;

    } catch (error) {
        console.error(`Error fetching:`, error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '-',
            message: `FileName: api-action.js, Fun: fetchFiles, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        if (error?.response?.status === 401) {
            cookies.remove('accessToken');
            localStorage.removeItem('accessToken');
            window.open(`/app`, "_self");
        }
        // 429 is a TRANSIENT rate limit, never an auth failure — clearing the token here silently signed
        // users out mid-session (see the note in fetchAPI). Surface it and let the caller retry.
        // Optional chaining is required: axios leaves `response` undefined when the request never reached
        // the server (offline, DNS, timeout), so the bare `error.response.status` threw a TypeError out of
        // this catch and callers never received the `{ error: ... }` value below — a failed blob fetch
        // surfaced as an unhandled rejection instead of a handled failure.
        if (error?.response?.status === 429) {
            toast.error('Too many requests — please wait a moment and try again.', {
                position: "top-center",
                transition: Bounce,
                // One toast, not one per in-flight request: the inbox fires several calls at once, so a
                // single rate-limited burst was stacking an identical toast for every one of them.
                toastId: 'rate-limited',
            });
        }

        return { error: "Something went wrong" };
    }
}
export async function getEncryptedValue(msg) {
    let token;
    try {
        token = await generateOpenApiToken();
    } catch (error) {
        // The token fetch is a raw fetch(), not fetchAPI, so a dropped connection REJECTS here instead of
        // returning the sentinel below. That rejection escaped getEncryptedValue, landed in the generic
        // catch of emailHelper's getEncryptedMessageByUser, failed the `instanceof EncryptionFailedError`
        // test and was swallowed — so that recipient's copy came back undefined, became "-" upstream and
        // was stripped out of the transaction while the user was told "Message Sent". Return the SAME
        // failure sentinel a failed /getEncryptValue produces: requireEncryptedValue already turns it into
        // an EncryptionFailedError that aborts the whole send and names the affected address.
        console.error('Error fetching openAPI token:', error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '/openapi-token',
            message: `FileName: api-action.js, Fun: getEncryptedValue, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return {
            __error: true,
            status: null,
            message: error?.message || 'the encryption service could not be reached'
        };
    }
    return await fetchAPI('getEncryptValue', 'POST', { msg }, token);
}
export async function getS3Data(urls) {
    return await fetchFiles('getS3Files', 'POST', { urls });
}

/**
 * SMTP delivery for the recipients who have no on-chain key. to/cc/bcc are the SMTP ENVELOPE, not just
 * headers: the server derives RCPT TO from them, so anything listed here receives a cleartext copy.
 * emailHelper.sendEmails is what narrows the recipient set down to those addresses — see the delivery-set
 * comment there before changing this signature.
 */
export async function sendWebTwoEmail(emailObject) {
    const [subject, from, to, cc, bcc, message, attachment] = emailObject;

    const formData = new FormData();

    // `attachment` is null on every send that carries no files — sendEmails defaults files = null and
    // AddEmailModal passes null explicitly — so the old `attachment.length` threw a TypeError before the
    // request was ever built, and an external-only send from that modal failed with nothing sent. Entries
    // can be null too (sendInvite builds [file] with file = null when there is no event), and FormData
    // would otherwise append those as the literal string "null".
    for (const file of attachment || []) {
        if (file) formData.append('files', file);
    }

    formData.append('subject', subject);
    formData.append('from', from);
    formData.append('to', to);
    formData.append('cc', cc);
    formData.append('bcc', bcc);
    formData.append('message', message);
    // `forwardEmails` is deliberately NOT sent. On the server it is an envelope OVERRIDE — a non-empty
    // value replaces the whole RCPT TO list, so the real external recipient in `to` received nothing while
    // only the forward/alias targets were delivered. Those targets are now carried as ordinary recipients
    // in to/bcc by sendEmails, which is what the client meant by the field all along.

    try {
        const token = getValidAccessToken();
        const response = await axios.post(`${BASE_URL}/sendEmail`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });
        return response;
    } catch (error) {
        console.error('Error uploading files', error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '-',
            message: `FileName: api-action.js, Fun: sendWebTwoEmail, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        if (error?.response?.status === 401) {
            cookies.remove('accessToken');
            localStorage.removeItem('accessToken');
            window.open(`/app`, "_self");
        }
        // 429 is a TRANSIENT rate limit, never an auth failure — clearing the token here silently signed
        // users out mid-session (see the note in fetchAPI). Surface it and let the caller retry.
        // Optional chaining is required: axios leaves `response` undefined when the request never reached
        // the server (offline, DNS, timeout), which is exactly what a dropped connection mid-send looks
        // like. The bare `error.response.status` then threw a TypeError out of this catch instead of
        // returning null, so the send never reported failure and the already-composed mail was parked in
        // the outbox — where a retry sends it twice.
        if (error?.response?.status === 429) {
            toast.error('Too many requests — please wait a moment and try again.', {
                position: "top-center",
                transition: Bounce,
                // One toast, not one per in-flight request: the inbox fires several calls at once, so a
                // single rate-limited burst was stacking an identical toast for every one of them.
                toastId: 'rate-limited',
            });
        }
        return null;
    }

}

export async function sendDecryptFile(file) {
    return await fetchAPI('decryptFile', 'POST', { stringfiedFile: file });
}

export async function sendAttachment(files, key, orgUser) {
    const attachmentResult = await encryptFiles(files, key);
    return attachmentResult;
}

export async function stringToFile(file, fileName) {

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);

    try {
        const token = getValidAccessToken();
        const response = await axios.post(`${BASE_URL}/stringToFile`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            responseType: 'blob'
        });
        return response;

    } catch (error) {
        console.error('Error uploading files', error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '/stringToFile',
            message: `FileName: api-action.js, Fun: stringToFile, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        if (error?.response?.status === 401) {
            cookies.remove('accessToken');
            localStorage.getItem('accessToken')
            window.open(`/app`, "_self");
        }
        // 429 is a TRANSIENT rate limit, never an auth failure — clearing the token here silently signed
        // users out mid-session (see the note in fetchAPI). Surface it and let the caller retry.
        // Optional chaining is required: axios leaves `response` undefined when the request never reached
        // the server (offline, DNS, timeout), and the bare `error.response.status` then threw a TypeError
        // out of this catch. This is the legacy-attachment fallback in attachmentHelper.js, so the throw
        // replaced "attachment unavailable" with a broken viewer.
        if (error?.response?.status === 429) {
            toast.error('Too many requests — please wait a moment and try again.', {
                position: "top-center",
                transition: Bounce,
                // One toast, not one per in-flight request: the inbox fires several calls at once, so a
                // single rate-limited burst was stacking an identical toast for every one of them.
                toastId: 'rate-limited',
            });
        }
    }
}


export async function getS3File(name, url) {
    return await fetchAPI('getS3Data', 'POST', { name, url });
}

export async function getContactsValue(uuid) {
    return await fetchAPI('getContactDetails', 'POST', { uuid });
}

/**
 * The signed-in user's contacts, from the database.
 *
 * `dcontact` used to have no column saying WHOSE contact a row was, so clients learned that one fact
 * from the contacts contract (getContactsCount / getEncryptedContacts) before a single body was read.
 * Migration 25 added the owner column and /getEncryptContactsValue records it as it writes, so the
 * chain index is no longer the source of truth — and contacts saved since that migration were never
 * written to the chain at all, which is why a chain-indexed read now hides them.
 *
 * The owner is taken from the JWT server-side and is never sent in the body, so this cannot read
 * another user's address book. `limit` is clamped server-side to 1..1000.
 */
export async function listContactsFromServer(offset = 0, limit = 500) {
    return await fetchAPI('dcontacts/list', 'POST', { offset, limit });
}

/**
 * Hand the server uuids the chain says are ours, for contacts saved before the owner column existed.
 * The server only adopts rows that belong to nobody, so this fills in history and cannot take a
 * contact away from another user.
 */
export async function claimContactsOnServer(uuid) {
    return await fetchAPI('dcontacts/claim', 'POST', { uuid });
}

export async function getInsecure(user_name) {
    return await fetchAPI('getInsecureEmails', 'POST', { user_name });
}

export async function getSecureEmail(uuid) {
    return await fetchAPI('getSecureEmail', 'POST', { uuid });
}

export async function updateInsecureMail(type, id, value) {
    return await fetchAPI('updateInsecureMail', 'POST', { type, id, value });
}

export async function createWeb2User(user_name) {
    return await fetchAPI('createWeb2User', 'POST', { user_name });
}

// Proof-of-possession: fetch a single-use server nonce for (user_name, publicKey). The caller signs the
// returned `challenge` with the wallet and passes the signature to login() below, proving control of the
// private key. Pre-auth, and both fields are public values.
// Use the /dAuth-prefixed path: the deployed gateway forwards /dAuth/* and /login, but NOT a bare
// /login-challenge (404).
export async function getLoginChallenge(user_name, publicKey) {
    return await fetchAPI('dAuth/login-challenge', 'POST', { user_name, publicKey });
}

export async function login(user_name, publicKey, couponCode = null, signature = null, signerPublicKey = null) {
    // Proof-of-possession is REQUIRED on every login, and this is the last place that can enforce it.
    // Callers build the proof with buildLoginProof() (auth/loginProof.js) and pass it through; a caller
    // that skips that step must fail HERE rather than reach the server with a bare (user_name, publicKey)
    // pair, which is public information and therefore no proof of anything. Guarding at the boundary means
    // no future call site can reintroduce an unsigned login by accident. Note this returns before the
    // organizationData clear below — an attempt that was never a valid login must not disturb local state.
    if (!signature || !signerPublicKey) {
        return { isAuth: false, isAccess: false, message: 'Wallet proof-of-possession is required to sign in.' };
    }
    await db.table('organizationData').clear();
    return await fetchAPI('login', 'POST', {
        user_name,
        publicKey,                                    // ML-KEM (identity/encryption) key — token identity
        appName: 'dMail',
        couponCode,
        // Accounts with no on-chain "assetAddress" attribute are rejected by the backend ("Wallet does not
        // match this account") — that is intended; those users must create a new account. There is
        // deliberately no unsigned fallback.
        signature,
        signerPublicKey,                              // ML-DSA (asset) signing key — PoP verify + address bind
    });
}

export async function getSymmetricKey(params) {
    try {
        return await fetchAPI(`dcontacts/getSymmetricKey`, 'POST', params);
    } catch (error) {
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: 'dcontacts/getSymmetricKey',
            message: String(`src\service\api-actions.js - ${error.message} - FUN: getSymmetricKey` || 'Error not handle'),
            error: errorStr,
            userId: userName || '',
        });
        return null;
    }
}

/**
 * dMail's OWN wrapped DEK (app_id "dmail") — separate from the dContacts key read by getSymmetricKey
 * above. Identity is taken from the JWT server-side; userName is only the lookup hint.
 *
 * saveDmailSymmetricKey is WRITE-ONCE: if a key already exists the server returns it UNCHANGED rather
 * than overwriting (an overwrite would permanently orphan every mail encrypted under the old DEK).
 * Callers must re-read after saving and adopt whatever actually persisted.
 *
 * These two deliberately let failures propagate instead of swallowing them like getSymmetricKey does:
 * dekManager must be able to tell "the user has no key yet" (mint) apart from "the request failed"
 * (retry) — collapsing both to null would mint a second, wrong DEK.
 */
export async function saveDmailSymmetricKey(userName, encryptedKey) {
    return await fetchAPI('dmail/symmetric-key', 'POST', { userName, encryptedKey });
}

export async function getDmailSymmetricKey(params) {
    return await fetchAPI('dmail/getSymmetricKey', 'POST', params);
}

// Coupon validation
export async function validateCoupon(couponCode) {
    return await fetchAPI('dsuite/validateCoupon', 'POST', { couponCode });
}

//Administration Emails Checking
export async function checkAdminEmails(emails) {
    return await fetchAPI('checkEmail', 'POST', {email: emails});
}

// export async function log(message, type = null) {
    // const user = cookies.get("userObject");
    // const user_name = user?.name ?? '-';
    // return await fetchAPI('log', 'POST', { message, type, user_name });
// }

export async function getEncryptedMessage(uuid, userName) {
    return await fetchAPI('getEncryptedMessage', 'POST', { uuid, userName });
}

export async function getOrganizationData(domain, userName) {
    return await fetchAPI('dsuite/getOrganizationDataByDomain', 'POST', { domain, userName });
}

export async function storeSendCopyEmailsInAdmin(emailData, organizationId, adminUserEmail) {
    return await fetchAPI('dsuite/storeSendCopyEmailsInAdmin', 'POST', { emailData, organizationId, adminUserEmail });
}

export async function decryptDSuiteUserData(encryptData, isAttachments = false) {
    return await fetchAPI('decryptDSuiteUserData', 'POST', { encryptData, isAttachments });
}

export async function sendConfirmationEmail(appointmentBookingId) {
    return await fetchAPI('sendConfirmationAppointmentEmail', 'POST', { appointmentBookingId })
}

export async function updateBookingSlotStatus(params) {
    return await fetchAPI('updateAppointmentBookingSlot', 'POST', { ...params });
}

export async function getForwardEmailDetails(organizationId) {
    return await fetchAPI('dsuite/fetchForwardEmailSettings', 'POST', { organizationId });
}

export async function createZoomMeeting(eventDetails) {
    try {
        return await fetchAPI('zoom/create/meetings', 'POST', { ...eventDetails });
    } catch (error) {
        console.error('Error during createZoomMeeting', error);
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '/zoom/create/meetings',
            message: `FileName: api-action.js, Fun: createZoomMeeting, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return null;
    }
}

export async function createGoogleMeetEvent(eventDetails) {
    try {
        return await fetchAPI('google/create-event', 'POST', { ...eventDetails });
    } catch (error) {
        console.error('Error during createGoogleMeeting', error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '/google/create-event',
            message: `FileName: api-action.js, Fun: createGoogleMeetEvent, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return null;
    }
}

export async function meetAppsStatus() {
    try {
        return await fetchAPI('calendarIntgrationStatus', 'GET');
    } catch (error) {
        console.error('Error during meetAppsStatus', error);
        const errorStr = toErrorString(error || error.message);
        const userName = localStorage.getItem('UserName');
        reportError({
            endpoint: '/calendarIntgrationStatus',
            message: `FileName: api-action.js, Fun: meetAppsStatus, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return null;
    }
}

export async function generatePresignedUrl(fileName, fileType = 'txt', type = 'put') {
    try {
        return await fetchAPI("generatePresignedUrl", "POST", { fileName, fileType, type });
    } catch (error) {
        console.error('Error during meetAppsStatus', error);
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '/generatePresignedUrl',
            message: `FileName: api-action.js, Fun: generatePresignedUrl, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return null;
    }
}